import { FormEvent, useState } from 'react';

import { Input } from '@/components/form/Input';
import { Inputs } from '@/components/form/Inputs';
import { Modal } from '@/components/Modal';
import { useModifyAccount } from '@/hooks';
import { Account } from '@/types';

interface SSORotateCredentialsModalProps {
  ssoAccount: Account | null;
  onClose: () => void;
  email?: string;
}

export function SSORotateCredentialsModal({
  ssoAccount,
  onClose,
  email,
}: SSORotateCredentialsModalProps) {
  const formKey = ssoAccount?.key ?? '';

  const [isSubmitting, setIsSubmitting] = useState(false);

  return (
    <Modal
      open={!!ssoAccount}
      onClose={onClose}
      title={`Rotate Credentials: ${ssoAccount?.member?.split(':')[1] || ''}`}
      size="lg"
      footer={{
        form: 'sso-rotate-credentials-form',
        text: isSubmitting ? 'Rotating...' : 'Rotate Credentials',
        disabled: isSubmitting,
      }}
      data-testid="sso-rotate-credentials-modal"
    >
      {ssoAccount && (
        <SSORotateCredentialsFormContent
          key={formKey}
          ssoAccount={ssoAccount}
          onClose={onClose}
          email={email}
          onSubmitStatusChange={setIsSubmitting}
        />
      )}
    </Modal>
  );
}

function SSORotateCredentialsFormContent({
  ssoAccount,
  onClose,
  email,
  onSubmitStatusChange,
}: {
  ssoAccount: Account;
  onClose: () => void;
  email?: string;
  onSubmitStatusChange: (submitting: boolean) => void;
}) {
  const [clientId, setClientId] = useState('');
  const [secret, setSecret] = useState('');
  const [issuerUrl, setIssuerUrl] = useState('');

  const { mutateAsync: rotateCredentials, status } = useModifyAccount(
    'refreshToken',
    email
  );

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const config: Record<string, string> = {};
    if (clientId.trim()) config.id = clientId.trim();
    if (secret.trim()) config.secret = secret.trim();
    if (issuerUrl.trim()) config.issuer = issuerUrl.trim();

    if (Object.keys(config).length === 0) return;

    onSubmitStatusChange(true);
    try {
      await rotateCredentials({
        username: ssoAccount.member,
        key: ssoAccount.key,
        config,
      });
      onClose();
    } finally {
      onSubmitStatusChange(false);
    }
  };

  return (
    <form
      className="flex flex-col space-y-4"
      id="sso-rotate-credentials-form"
      onSubmit={handleSubmit}
    >
      <div className="bg-chariot-background-secondary rounded-lg p-4">
        <p className="text-chariot-text-secondary text-sm">
          Update your OIDC credentials. Only fields you fill in will be changed;
          leave a field blank to keep the current value.
        </p>
      </div>
      <Inputs
        inputs={[
          {
            label: 'Client ID',
            value: clientId,
            placeholder: 'New client ID (leave blank to keep current)',
            name: 'clientId',
          },
          {
            label: 'Client Secret',
            value: secret,
            type: Input.Type.PASSWORD,
            placeholder: 'New secret (leave blank to keep current)',
            name: 'secret',
          },
          {
            label: 'Issuer URL',
            value: issuerUrl,
            placeholder: 'New issuer URL (leave blank to keep current)',
            name: 'issuerUrl',
          },
        ]}
        onChange={values => {
          if ('clientId' in values) setClientId(values.clientId as string);
          if ('secret' in values) setSecret(values.secret as string);
          if ('issuerUrl' in values) setIssuerUrl(values.issuerUrl as string);
        }}
      />
      {status === 'pending' && (
        <p className="text-chariot-text-secondary text-sm">
          Rotating credentials...
        </p>
      )}
    </form>
  );
}
